import { IApplicationData } from 'types/applications';

import HeroImage from 'assets/applications/second/hero.webp';
import FirstBlockImage from 'assets/applications/app-2.0.webp';

import ThreeHelicoptersInFieldImage from 'assets/common/images/aeronautics-use-case-3.webp';
import HelicopterWithThreeDronesImage from 'assets/customer-cases/air-bus-helicopter/helicopter-with-three-drones.webp';
import ThreeHelicoptersImage from 'assets/customer-cases/air-bus-helicopter/three-helicopters.webp';
import TwoHelicoptersInForestImage from 'assets/customer-cases/air-bus-helicopter/two-helicopters-in-forest.webp';

import AutomationIcon from 'assets/applications/icons/automation.svg';
import DeveloperModeIcon from 'assets/applications/icons/developer-mode.svg';

import BrainIcon from 'assets/applications/icons/why-choose-us/brain.svg';
import TickBoxIcon from 'assets/applications/icons/why-choose-us/tick-box.svg';
import CircleAroundIcon from 'assets/applications/icons/why-choose-us/circle-around.svg';
import BalanceIcon from 'assets/applications/icons/why-choose-us/balance.svg';
import ThomasKImage from 'assets/avatars/ThomasK.svg';
import MichaelGrantImage from 'assets/avatars/MichaelGrant.svg';
import { airbusHelicopterCustomerCaseMeta } from 'constants/pages';

export const secondApplicationData: IApplicationData = {
  hero: {
    title: 'Intelligent Data Pipeline with Simulation-in-the-Loop',
    description: `
      Collecting and labeling real-world data is slow, expensive, and often fails to capture 
      the full complexity of real operational environments. Build a smarter, more robust data 
      strategy by integrating intelligent simulation into your AI data pipeline. AILiveSim
      helps you generate high-quality, relevant data on demand, improving model performance 
      while saving time and cost.
    `,
    videoSrc: 'https://ailivesim.com/Videos/AILiveSim_Navy.mp4',
    imageSrc: HeroImage,
  },
  firstTextImageData: {
    title: 'Challenges in Data Collection',
    description: `
      Collecting real-world data for AI training is expensive, slow, 
      and often incomplete. Manual scenario creation can introduce bias, 
      while rare or high-risk situations are difficult to capture. AILiveSim
      eliminates these bottlenecks by generating high-quality, targeted 
      simulation data on demand — ensuring full coverage, reduced costs, and 
      faster AI model improvement.
    `,
    imageSrc: FirstBlockImage,
    listItems: [
      'High costs and delays from collecting and labeling real-world data',
      'Manual scenario creation results in incomplete or biased datasets',
      'Difficulty capturing edge cases and achieving test coverage',
      'Costly, inefficient data collection campaigns',
    ],
  },
  informationData: {
    title: 'How AILiveSim Enhances Your Data Pipeline',
    description: `
      AILiveSim brings simulation-driven intelligence to your data pipeline, locally or cloud-based, automated, and fully scalable to your needs.
    `,
    items: [
      {
        imageSrc: AutomationIcon,
        title: '“Set and forget” automation',
        description:
          'Once integrated, AILiveSim runs continuously within your pipelines, delivering consistent improvements.',
      },
      {
        imageSrc: DeveloperModeIcon,
        title: 'Generate only what’s needed',
        description:
          'The system identifies and generates the right data, exactly when it’s required.',
      },
    ],
    listItems: [
      'Intelligent, on-demand data generation in the cloud or on-premise',
      'Seamless integration with your existing MLOps',
      'AI training AI, with minimal manual oversight',
    ],
    listTitle: 'Key Capabilities of AiLS for Data Pipeline Optimization',
  },
  whyChooseUsData: {
    title: 'Why Teams Choose\nAILiveSim for Smarter AI\nData Pipelines',
    blueBoxTitle:
      'Make simulation an always-on, intelligent part of your AI data strategy — improve coverage, save time, and boost model reliability.',
    items: [
      {
        title: 'Automatically Enrich Datasets Without Manual Collection',
        description:
          'Generate diverse, high-quality sensor data using AI simulation: no need for costly or time-consuming real-world collection.',
        imageSrc: BrainIcon,
      },
      {
        title: 'Continual Model Improvement with Simulated Data',
        description:
          'Refine and retrain models faster using fresh, targeted simulation data—anytime, at scale.',
        imageSrc: CircleAroundIcon,
      },
      {
        title: 'Fix OOD Issues & Improve Data Coverage',
        description:
          'Spot out-of-distribution gaps and enrich your dataset with tailored simulations for better performance.',
        imageSrc: TickBoxIcon,
      },
      {
        title: 'Balance Quality and Cost',
        description:
          'Set performance or budget thresholds to optimize automation based on real needs.',
        imageSrc: BalanceIcon,
      },
    ],
  },
  secondTextImageData: {
    title: 'Customer Case: Advancing Helicopter Safety through Next-Gen Simulation',
    description: `
      From low-altitude flights near power lines to foggy urban skies, discover how AILiveSim helped a global OEM validate AI-driven anti-collision systems in conditions once considered untestable.
    `,
    images: {
      firstImage: {
        imageSrc: ThreeHelicoptersInFieldImage,
        altText: 'Three helicopters in field',
      },
      secondImage: {
        imageSrc: HelicopterWithThreeDronesImage,
        altText: 'Helicopter with three drones',
      },
      thirdImage: {
        imageSrc: ThreeHelicoptersImage,
        altText: 'Three helicopters',
      },
      fourthImage: {
        imageSrc: TwoHelicoptersInForestImage,
        altText: 'Two helicopters in forest',
      },
    },
    link: airbusHelicopterCustomerCaseMeta.url,
  },
  carouselData: [
    {
      authorImage: MichaelGrantImage,
      authorName: 'Michael Grant',
      authorRole: 'Innovation Lead at a major maritime OEM',
      text: '“Simulation allowed us to iterate quickly, validate safely, and deliver an intelligent co-captain experience the market hadn’t seen before.”',
    },
    {
      authorImage: ThomasKImage,
      authorName: 'Thomas K.',
      authorRole: 'Program Lead, Helicopter OEM',
      text: `"AILiveSim allowed us to simulate the unpredictable—from night-time flights near power lines to low-visibility urban environments. It's the only solution that offered the
        variability and realism our project demanded."`,
    },
  ],
};
